// Vite resolves this `?url` virtual module to the bundled worker URL at build
// time; eslint-plugin-import can't follow the query suffix, so it's disabled here.
// eslint-disable-next-line import/no-unresolved, import/extensions
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.js?url";

// Rendered at 2x so the rasterized signature stays crisp on the certificate.
const PDF_RENDER_SCALE = 2;

/**
 * Rasterizes the first page of a PDF into a PNG file.
 *
 * Signatures are rendered as `<img>` everywhere (certificate, sign-task modal,
 * previews), and a PDF can't be shown that way — so a PDF signature is
 * flattened to an image at upload time. Only the first page is used; that's
 * where a signature lives.
 */
const rasterizePdfFirstPage = async (file: File): Promise<File> => {
  // Loaded on demand so pdf.js (a heavy lib) only ships to users who actually
  // upload a PDF signature, instead of bloating every route that can create one.
  const pdfjsLib = await import("pdfjs-dist");
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

  const data = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data }).promise;

  try {
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: PDF_RENDER_SCALE });

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) {
      throw new Error("Could not get a 2D canvas context for PDF rendering");
    }

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    // PDFs have no alpha channel — paint white so the signature isn't rendered
    // onto a transparent/black background.
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvasContext: context, viewport }).promise;

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/png")
    );

    if (!blob) {
      throw new Error("Failed to convert the PDF page to a PNG image");
    }

    return new File([blob], "signature.png", { type: "image/png" });
  } finally {
    await pdf.destroy();
  }
};

/**
 * Builds the file to upload for a signature.
 *
 * PDFs are flattened to a PNG of their first page; images are uploaded as-is.
 * Signature images are intentionally NOT compressed or resized on the client so
 * the stored signature keeps its original quality.
 */
const buildSignatureUploadFile = async (
  file: File,
  title: string
): Promise<File> => {
  const imageFile =
    file.type === "application/pdf" ? await rasterizePdfFirstPage(file) : file;

  return new File([imageFile], title, { type: imageFile.type });
};

export default buildSignatureUploadFile;
